import { BPA, DEPT, DIV, RESULT, QUARTILE, PERSONNEL, NONPERSONNEL, REVENUE, FUND, SERVICE_TYPE, PROGRAM, PRIORITY } from '../../static/keyWords.js';
import { QUARTILE_DICTIONARY } from '../../static/constants.js';
import { FILTER_TYPES } from '../reusable/Filters/filterHelpers.js';

const invalidCharactersRegex = /[ :/\\[\]+*=$>#^&()-]/g;
const getHeaderTabs = (nameConventions) => {
  return [
    {name: `${nameConventions[PROGRAM].alias} cost details`},
    {name: 'Compare'},
    {name: 'Cost type'},
    {name: 'Expense and revenue'},
    {name: `${nameConventions[PRIORITY].alias} spending`},
  ];
};

const SUB_FILTER_TO_PROGRAM_LABELS = {
  [DEPT]: 'department',
  [DIV]: 'division',
  [SERVICE_TYPE]: 'serviceType',
  [FUND]: 'fund',
};

export const DRILL_DOWN_BAR_COLORS = {
  PROGRAM_COST: '#3D5AFE',
  COST_TYPE: '#80CBC4',
};

const PROGRAM_ACCOUNT_TYPES = ['personnel', 'nonPersonnel', 'revenue'];
const PROGRAM_ACCOUNT_TYPES_WITHOUT_REVENUE = ['personnel', 'nonPersonnel'];
export const TAB_PROGRAM_COST_DETAILS = 'TAB_PROGRAM_COST_DETAILS';
export const TAB_BUDGET_AND_FUND_COMPARE = 'TAB_BUDGET_AND_FUND_COMPARE';
export const TAB_COST_TYPE = 'COST_TYPE';
export const TAB_EXPENSE_AND_REVENUE = 'TAB_EXPENSE_AND_REVENUE';
export const TAB_RESULT_SPENDING = 'TAB_RESULT_SPENDING';
export const TAB_BUDGET_AND_FUND_COMPARE_BY_FUND = 'TAB_BUDGET_AND_FUND_COMPARE_BY_FUND';

const PROGRAM_COST_DETAILS_FILTERS = [
  FILTER_TYPES.DV_FUND,
  FILTER_TYPES.DV_DIVISION,
  FILTER_TYPES.DV_SECTION,
  FILTER_TYPES.SERVICE_TYPE,
  FILTER_TYPES.DV_QUARTILE,
  FILTER_TYPES.BPA,
  FILTER_TYPES.DV_PERSONNEL,
  FILTER_TYPES.DV_NONPERSONNEL,
  FILTER_TYPES.DV_REVENUE,
];
const BUDGET_AND_FUND_COMPARE_FILTERS = [FILTER_TYPES.DV_BUDGET, FILTER_TYPES.DV_DIVISION, FILTER_TYPES.DV_SECTION, FILTER_TYPES.DV_FUND, FILTER_TYPES.BPA];
const COST_TYPE_FILTERS = [
  FILTER_TYPES.DV_FUND,
  FILTER_TYPES.DV_DIVISION,
  FILTER_TYPES.DV_SECTION,
  FILTER_TYPES.DV_RESULT,
  FILTER_TYPES.BPA,
  FILTER_TYPES.DV_QUARTILE,
];
const EXPENSE_AND_REVENUE_FILTERS = [
  FILTER_TYPES.DV_FUND,
  FILTER_TYPES.DV_DIVISION,
  FILTER_TYPES.DV_SECTION,
  FILTER_TYPES.DV_RESULT,
  FILTER_TYPES.SERVICE_TYPE,
  FILTER_TYPES.BPA,
  FILTER_TYPES.DV_QUARTILE,
];
const RESULT_SPENDING_FILTERS = [FILTER_TYPES.DV_FUND, FILTER_TYPES.DV_DIVISION, FILTER_TYPES.DV_SECTION, FILTER_TYPES.DV_QUARTILE];
const TAB_BUDGET_AND_FUND_COMPARE_BY_FUND_FILTERS = [FILTER_TYPES.DV_DIVISION, FILTER_TYPES.DV_SECTION, FILTER_TYPES.DV_FUND, FILTER_TYPES.BPA];

export const DATA_VISUALIZATIONS_TAB_FILTERS = {
  [TAB_PROGRAM_COST_DETAILS]: PROGRAM_COST_DETAILS_FILTERS,
  [TAB_BUDGET_AND_FUND_COMPARE]: BUDGET_AND_FUND_COMPARE_FILTERS,
  [TAB_COST_TYPE]: COST_TYPE_FILTERS,
  [TAB_EXPENSE_AND_REVENUE]: EXPENSE_AND_REVENUE_FILTERS,
  [TAB_RESULT_SPENDING]: RESULT_SPENDING_FILTERS,
  [TAB_BUDGET_AND_FUND_COMPARE_BY_FUND]: TAB_BUDGET_AND_FUND_COMPARE_BY_FUND_FILTERS,
};

const filterData = (
  programs,
  selectedFilters,
  customGetProgramValueForFilter = getProgramValueForFilter,
  customFilterItemsOfProgram = filterItemsOfProgram
) => {
  let _programs = [...programs].filter((program) =>
    applyFiltersToProgram(program, selectedFilters, customGetProgramValueForFilter, customFilterItemsOfProgram)
  );
  return _programs;
};

const applyFiltersToProgram = (
  program,
  selectedFilters,
  customGetProgramValueForFilter = getProgramValueForFilter,
  customFilterItemsOfProgram = filterItemsOfProgram
) => {
  let showProgram = true;

  Object.entries(selectedFilters).forEach(([tagFilterName, subFilters]) => {
    Object.entries(subFilters).forEach(([subFilterLabel, selected]) => {
      if (tagFilterName === DEPT || tagFilterName === DIV || (tagFilterName === FUND && !(SUB_FILTER_TO_PROGRAM_LABELS[FUND] in program))) {
        let atLeastOneItemLeft = customFilterItemsOfProgram(program, subFilterLabel, selected);
        if (!atLeastOneItemLeft) showProgram = false;
        return;
      }
      let programValueToCompare = customGetProgramValueForFilter(program, subFilterLabel, tagFilterName);
      if (!programValueToCompare) showProgram = false;
      else showProgram = showProgram && valueMatchesFilter(tagFilterName, selected, programValueToCompare);
    });
  });

  return showProgram;
};

const valueMatchesFilter = (tagFilterName, selected, value) => {
  if (tagFilterName === DEPT || tagFilterName === DIV || tagFilterName === QUARTILE || tagFilterName === SERVICE_TYPE) {
    return selected.includes(value);
  }
  // In this case we are comparing scoreOption objects
  else if (tagFilterName === BPA || tagFilterName === RESULT || tagFilterName === FUND) {
    return selected.some((selection) => (selection.name ? selection.name === value : selection === value));
  } else if (tagFilterName === PERSONNEL || tagFilterName === NONPERSONNEL || tagFilterName === REVENUE) {
    return selected.some((option) => value.includes(option));
  }
  return true;
};

const getProgramValueForFilter = (program, subFilterLabel, tagFilterName) => {
  // Extract the program info that needs to be compared to a specific filter
  if (tagFilterName === BPA) {
    return getProgramScore(program.bpaResults, subFilterLabel);
  }
  if (tagFilterName === RESULT) {
    return getProgramScore(program.results, subFilterLabel);
  } else return program[SUB_FILTER_TO_PROGRAM_LABELS[subFilterLabel]];
};

const getProgramScore = (resultsArray, subFilterLabel) => {
  let result = resultsArray && resultsArray.find((result) => result.name === subFilterLabel);
  if (result) return result.score;
};

const filterItemsOfProgram = (program, subFilterLabel, selected) => {
  //Filters program items in place and returns T/F depending on whether there are any items left
  let items = program.items;
  program.filteredItems = {};
  let atLeastOneItem = false;
  PROGRAM_ACCOUNT_TYPES.forEach((accountType) => {
    let filteredItems = filterItems(items[accountType], subFilterLabel, selected);
    program.filteredItems[accountType] = filteredItems;
    if (filteredItems.length > 0) atLeastOneItem = true;
  });
  return atLeastOneItem;
};

const filterItems = (items, subFilterLabel, selected) => {
  return items.filter((item) => selected.includes(item[SUB_FILTER_TO_PROGRAM_LABELS[subFilterLabel]]));
};

const QUARTILE_KEYS = ['mostAligned', 'moreAligned', 'lessAligned', 'leastAligned'];

const QUARTILE_KEY_TO_NAME = {
  [QUARTILE_KEYS[0]]: QUARTILE_DICTIONARY[1],
  [QUARTILE_KEYS[1]]: QUARTILE_DICTIONARY[2],
  [QUARTILE_KEYS[2]]: QUARTILE_DICTIONARY[3],
  [QUARTILE_KEYS[3]]: QUARTILE_DICTIONARY[4],
};
const QUARTILE_LABELS = [
  QUARTILE_DICTIONARY[1],
  QUARTILE_DICTIONARY[2],
  QUARTILE_DICTIONARY[3],
  QUARTILE_DICTIONARY[4],
];

const ObjIsEmpty = (obj) => {
  return Object.keys(obj).length === 0;
};

const getSelectedFiltersWithoutQuartile = (selectedFilters) => {
  return Object.keys(selectedFilters)
    .filter((key) => key != QUARTILE)
    .reduce((obj, key) => {
      obj[key] = selectedFilters[key];
      return obj;
    }, {});
};

const areFiltersEmpty = (filters) => {
  if (Object.keys(filters).length === 0) return true;

  return Object.values(filters).every((value) => ObjIsEmpty(value));
};

const wrapLabels = (labels) =>
  labels.map(label =>
    label.length >= 30
      ? `${label.substring(0, 30)}...`
      : label
  );

const createSortFunction = (property, isAscending) => {
  if (isAscending)
    return (data) =>
      data.sort((dataNode1, dataNode2) => {
        return dataNode1[property] >= dataNode2[property] ? 1 : -1;
      });

  return (data) =>
    data.sort((dataNode1, dataNode2) => {
      return dataNode1[property] <= dataNode2[property] ? 1 : -1;
    });
};

const isDepartmentOrDivisionFilterSelected = (selectedFilters) => {
  let departmentFilterSelected = selectedFilters[DEPT] && Object.keys(selectedFilters[DEPT]).length > 0;
  let divisionFilterSelected = selectedFilters[DIV] && Object.keys(selectedFilters[DIV]).length > 0;

  return departmentFilterSelected || divisionFilterSelected;
};

const getTabFilters = (idx) => {
  switch (idx) {
    case 0:
      return DATA_VISUALIZATIONS_TAB_FILTERS[TAB_PROGRAM_COST_DETAILS];
    case 1:
      return DATA_VISUALIZATIONS_TAB_FILTERS[TAB_BUDGET_AND_FUND_COMPARE];
    case 2:
      return DATA_VISUALIZATIONS_TAB_FILTERS[TAB_COST_TYPE];
    case 3:
      return DATA_VISUALIZATIONS_TAB_FILTERS[TAB_EXPENSE_AND_REVENUE];
    case 4:
      return DATA_VISUALIZATIONS_TAB_FILTERS[TAB_RESULT_SPENDING];
    case 5:
      return DATA_VISUALIZATIONS_TAB_FILTERS[TAB_BUDGET_AND_FUND_COMPARE_BY_FUND];
  }
};

const getTabDataName = (currentTab, nameConventions) => {

  switch (currentTab) {
    case 0:
    case 3:
      return nameConventions[PROGRAM].pluralFormAlias;
    case 1:
      return 'Comparison';
    case 2:
      return 'Line items';
    case 4:
      return nameConventions[PRIORITY].pluralFormAlias;
  }
};

const getSortingOptions = (currentTab) => {
  switch (currentTab) {
    case 0:
    case 2:
      return [
        { label: 'Sort by label A-Z', value: 'sortAsc' },
        { label: 'Sort by label Z-A', value: 'sortDesc' },
        { label: 'Sort by value low-high', value: 'sortValueAsc' },
        { label: 'Sort by value high-low', value: 'sortValueDesc' },
      ];
    case 3:
      return [
        { label: 'Sort by label A-Z', value: 'sortAsc' },
        { label: 'Sort by label Z-A', value: 'sortDesc' },
        { label: 'Sort by expense value low-high', value: 'sortExpenseValueAsc' },
        { label: 'Sort by expense value high-low', value: 'sortExpenseValueDesc' },
        { label: 'Sort by revenue value low-high', value: 'sortRevenueValueAsc' },
        { label: 'Sort by revenue value high-low', value: 'sortRevenueValueDesc' },
      ];
    default:
      break;
  }
};

const sortGraphData = (data, sortByProperty) => {
  if (!data || !data.children) return data;
  if (data.children.length === 0) return data;
  switch (sortByProperty) {
    case 'sortAsc':
      return { ...data, children: data.children.sort((a, b) => a.label.localeCompare(b.label)) };
    case 'sortDesc':
      return { ...data, children: data.children.sort((a, b) => b.label.localeCompare(a.label)) };
    case 'sortValueAsc':
      return { ...data, children: data.children.sort((a, b) => a.value - b.value) };
    default:
      return { ...data, children: data.children.sort((a, b) => b.value - a.value) };
  }
};

export {
  filterData,
  getProgramScore,
  applyFiltersToProgram,
  QUARTILE_KEY_TO_NAME,
  QUARTILE_KEYS,
  ObjIsEmpty,
  getSelectedFiltersWithoutQuartile,
  areFiltersEmpty,
  wrapLabels,
  createSortFunction,
  PROGRAM_ACCOUNT_TYPES,
  SUB_FILTER_TO_PROGRAM_LABELS,
  isDepartmentOrDivisionFilterSelected,
  invalidCharactersRegex,
  PROGRAM_ACCOUNT_TYPES_WITHOUT_REVENUE,
  getHeaderTabs,
  getTabFilters,
  getTabDataName,
  getSortingOptions,
  sortGraphData,
  QUARTILE_LABELS,
};
